import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useGallery,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Smart photo selection
//
// Each card shows a DISTINCT, category-appropriate property photo pulled from
// the photo gallery: an interior for "Floor Plans", an amenity/pool shot for
// "Amenities", and a lifestyle/common-space shot for the "Gallery". The gallery
// has only free-text metadata, so — like HeroImageBG / TabbedCallouts — we
// score each photo by keyword hits and pick the best unused match, falling back
// to a fixed index so every card still resolves to an image.
// ============================================================================

const KEYWORDS = {
  interior: [
    'interior', 'apartment', 'unit', 'living', 'living room', 'kitchen', 'bedroom',
    'bath', 'dining', 'home', 'closet', 'hardwood', 'granite', 'stainless', 'floor plan',
    'window', 'cabinet', 'counter',
  ],
  amenities: [
    'amenity', 'pool', 'swim', 'fitness', 'gym', 'clubhouse', 'lounge', 'spa', 'sauna',
    'courtyard', 'grill', 'bbq', 'cabana', 'sundeck', 'rooftop', 'deck', 'fire pit',
  ],
  lifestyle: [
    'lounge', 'clubhouse', 'game', 'billiard', 'bar', 'lobby', 'entrance', 'social',
    'resident', 'entertainment', 'coffee', 'theater', 'community', 'luxury',
  ],
};

function normalizeUrl(url: string): string {
  try {
    const u = new URL(url);
    return (u.origin + u.pathname).replace(/\/+$/, '').toLowerCase();
  } catch {
    return url.toLowerCase().split('?')[0].replace(/\/+$/, '');
  }
}

function scorePhoto(photo: PhotoGallery, keywords: string[]): number {
  const text = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!text) return 0;
  return keywords.reduce((s, kw) => s + (text.includes(kw) ? 1 : 0), 0);
}

/** Pick the best-scoring gallery index not already used by another card; fall
    back to `fallback` (wrapped into range) when nothing scores or the gallery
    is empty. */
function pickIndex(
  gallery: PhotoGallery[],
  keywords: string[],
  used: Set<number>,
  fallback: number,
): number {
  const wrappedFallback = gallery.length
    ? ((fallback % gallery.length) + gallery.length) % gallery.length
    : fallback;

  const seenUrls = new Set<string>();
  const scored: { index: number; score: number }[] = [];
  for (let i = 0; i < gallery.length; i++) {
    const photo = gallery[i];
    if (!photo.image || used.has(i)) continue;
    const key = normalizeUrl(photo.image);
    if (seenUrls.has(key)) continue;
    seenUrls.add(key);
    scored.push({ index: i, score: scorePhoto(photo, keywords) });
  }

  const matches = scored.filter((s) => s.score > 0).sort((a, b) => b.score - a.score);
  if (matches.length) return matches[0].index;
  // No keyword match — take the fallback if it's free, else the first unused photo.
  if (!used.has(wrappedFallback)) return wrappedFallback;
  const firstFree = scored[0];
  return firstFree ? firstFree.index : wrappedFallback;
}

// ============================================================================
// Slot group definitions
//
// Per card: one image group (bound to the smart-selected gallery photo) and one
// link group (the shared destination for both text lines). makeImageDef() binds
// the image to the resolved gallery index at runtime; the literal defs below
// are the static anchors the schema linter matches.
// ============================================================================

function makeImageDef(index: number, alt: string): SlotGroupDefinition {
  return {
    label: 'Background Image',
    fields: {
      image: { label: 'Image', type: 'image', defaultField: `photo_gallery[${index}].image` },
      alt: { label: 'Alt Text', type: 'text', defaultField: `photo_gallery[${index}].alt_text`, defaultValue: alt },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

const card1ImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text', defaultValue: 'Apartment interior' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const card2ImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[4].alt_text', defaultValue: 'Community amenities' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 4, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const card3ImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[8].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[8].alt_text', defaultValue: 'Community lifestyle' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 8, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const card1LinkDef: SlotGroupDefinition = {
  label: 'Card Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const card2LinkDef: SlotGroupDefinition = {
  label: 'Card Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const card3LinkDef: SlotGroupDefinition = {
  label: 'Card Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function HyperlocalExplore() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const gallery = useGallery();

  // Resolve three distinct, category-appropriate photo indices up front so no
  // two cards share the same image.
  const [i1, i2, i3] = useMemo(() => {
    const used = new Set<number>();
    const a = pickIndex(gallery, KEYWORDS.interior, used, 0);
    used.add(a);
    const b = pickIndex(gallery, KEYWORDS.amenities, used, 4);
    used.add(b);
    const c = pickIndex(gallery, KEYWORDS.lifestyle, used, 8);
    used.add(c);
    return [a, b, c];
  }, [gallery]);

  const img1Def = useMemo(() => makeImageDef(i1, 'Apartment interior'), [i1]);
  const img2Def = useMemo(() => makeImageDef(i2, 'Community amenities'), [i2]);
  const img3Def = useMemo(() => makeImageDef(i3, 'Community lifestyle'), [i3]);

  const img1 = useSlotGroup('hyperlocalexplore.card1_image', img1Def);
  const img2 = useSlotGroup('hyperlocalexplore.card2_image', img2Def);
  const img3 = useSlotGroup('hyperlocalexplore.card3_image', img3Def);

  const link1 = useSlotGroup('hyperlocalexplore.card1_link', card1LinkDef);
  const link2 = useSlotGroup('hyperlocalexplore.card2_link', card2LinkDef);
  const link3 = useSlotGroup('hyperlocalexplore.card3_link', card3LinkDef);

  return (
    <section ref={sectionRef} className="reveal-fade-up hle-section">
      <div className="hle-grid">
        {/* Card 1 — Floor Plans */}
        <div className="hle-card group">
          <EditableSlotGroup groupPath="hyperlocalexplore.card1_image" definition={img1Def}>
            <div className="hle-card__bg">
              {img1.image ? (
                <VibesitesImage
                  url={img1.image}
                  alt={img1.alt || 'Apartment interior'}
                  sizes="(min-width: 768px) 33vw, 100vw"
                  className="hle-card__img"
                />
              ) : (
                <div className="hle-card__img hle-card__img--placeholder" />
              )}
            </div>
          </EditableSlotGroup>
          <div className="hle-card__overlay" aria-hidden="true" />
          <EditableSlotGroup groupPath="hyperlocalexplore.card1_link" definition={card1LinkDef}>
            <div className="hle-card__content">
              <a href={link1.url || '/floor-plans'} target={link1._target || '_self'} className="hle-card__link">
                <EditableToken path="hyperlocalexplore.card1_title" as="span" className="hle-card__title">
                  Superior Spaces
                </EditableToken>
              </a>
              <a href={link1.url || '/floor-plans'} target={link1._target || '_self'} className="hle-card__link">
                <EditableToken path="hyperlocalexplore.card1_subtitle" as="span" className="hle-card__subtitle">
                  Browse Our Floor Plans
                </EditableToken>
              </a>
            </div>
          </EditableSlotGroup>
        </div>

        {/* Card 2 — Amenities */}
        <div className="hle-card group">
          <EditableSlotGroup groupPath="hyperlocalexplore.card2_image" definition={img2Def}>
            <div className="hle-card__bg">
              {img2.image ? (
                <VibesitesImage
                  url={img2.image}
                  alt={img2.alt || 'Community amenities'}
                  sizes="(min-width: 768px) 33vw, 100vw"
                  className="hle-card__img"
                />
              ) : (
                <div className="hle-card__img hle-card__img--placeholder" />
              )}
            </div>
          </EditableSlotGroup>
          <div className="hle-card__overlay" aria-hidden="true" />
          <EditableSlotGroup groupPath="hyperlocalexplore.card2_link" definition={card2LinkDef}>
            <div className="hle-card__content">
              <a href={link2.url || '/amenities'} target={link2._target || '_self'} className="hle-card__link">
                <EditableToken path="hyperlocalexplore.card2_title" as="span" className="hle-card__title">
                  Indulgent Details
                </EditableToken>
              </a>
              <a href={link2.url || '/amenities'} target={link2._target || '_self'} className="hle-card__link">
                <EditableToken path="hyperlocalexplore.card2_subtitle" as="span" className="hle-card__subtitle">
                  Explore Our Amenities
                </EditableToken>
              </a>
            </div>
          </EditableSlotGroup>
        </div>

        {/* Card 3 — Gallery */}
        <div className="hle-card group">
          <EditableSlotGroup groupPath="hyperlocalexplore.card3_image" definition={img3Def}>
            <div className="hle-card__bg">
              {img3.image ? (
                <VibesitesImage
                  url={img3.image}
                  alt={img3.alt || 'Community lifestyle'}
                  sizes="(min-width: 768px) 33vw, 100vw"
                  className="hle-card__img"
                />
              ) : (
                <div className="hle-card__img hle-card__img--placeholder" />
              )}
            </div>
          </EditableSlotGroup>
          <div className="hle-card__overlay" aria-hidden="true" />
          <EditableSlotGroup groupPath="hyperlocalexplore.card3_link" definition={card3LinkDef}>
            <div className="hle-card__content">
              <a href={link3.url || '/gallery'} target={link3._target || '_self'} className="hle-card__link">
                <EditableToken path="hyperlocalexplore.card3_title" as="span" className="hle-card__title">
                  Luxury Living
                </EditableToken>
              </a>
              <a href={link3.url || '/gallery'} target={link3._target || '_self'} className="hle-card__link">
                <EditableToken path="hyperlocalexplore.card3_subtitle" as="span" className="hle-card__subtitle">
                  View the Gallery
                </EditableToken>
              </a>
            </div>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .hle-section { width: 100%; background: #ffffff; }
        .hle-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.75rem;
          padding: 0.75rem;
        }
        /* Tablet: 3-up so all three cards fit on one row. */
        @media (min-width: 768px) {
          .hle-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 1rem;
            padding: 1rem;
          }
        }
        .hle-card {
          position: relative;
          overflow: hidden;
          min-height: 260px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        /* Tablet: taller 4:3 so the narrower 3-up cards keep enough height for
           the title + subtitle without clipping. */
        @media (min-width: 768px) {
          .hle-card { min-height: 0; aspect-ratio: 4 / 3; }
        }
        @media (min-width: 1024px) {
          /* ~16:9 landscape ratio to match the reference screenshot */
          .hle-card { aspect-ratio: 16 / 9; }
        }
        .hle-card__bg { position: absolute; inset: 0; }
        .hle-card__img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .hle-card:hover .hle-card__img { transform: scale(1.05); }
        .hle-card__img--placeholder { background: var(--vibe-color-text); }
        .hle-card__overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.42);
          transition: background 0.4s ease;
          pointer-events: none;
        }
        .hle-card:hover .hle-card__overlay { background: rgba(0, 0, 0, 0.52); }
        .hle-card__content {
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.85rem;
          text-align: center;
          padding: 1.5rem;
          color: #fff;
        }
        /* Both lines are links; explicitly no underline per the reference. */
        .hle-card__link {
          text-decoration: none;
          color: #fff;
          transition: opacity 0.2s ease;
        }
        .hle-card__link:hover { opacity: 0.85; }
        .hle-card__title {
          display: block;
          font-family: var(--vibe-font-heading);
          font-weight: 400;
          font-size: 1.1rem;
          line-height: 1.15;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          text-shadow: 0 1px 12px rgba(0, 0, 0, 0.45);
        }
        /* Tablet: smaller title so the narrower 3-up cards don't wrap. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .hle-card__title { font-size: 1rem; letter-spacing: 0.05em; }
        }
        @media (min-width: 1024px) {
          .hle-card__title { font-size: 1.4rem; }
        }
        .hle-card__subtitle {
          display: block;
          font-family: var(--vibe-font-heading);
          font-weight: 400;
          font-size: 0.9rem;
          line-height: 1.3;
          text-shadow: 0 1px 10px rgba(0, 0, 0, 0.45);
        }
        /* Tablet: smaller subtitle so the narrower 3-up cards don't wrap. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .hle-card__subtitle { font-size: 0.85rem; }
        }
        @media (min-width: 1024px) {
          .hle-card__subtitle { font-size: 1.05rem; }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'HyperlocalExplore',
  displayName: 'Hyperlocal Explore Links',
  tokens: {
    'hyperlocalexplore.card1_title': { label: 'Card 1 Title', type: 'text', defaultValue: 'Superior Spaces' },
    'hyperlocalexplore.card1_subtitle': { label: 'Card 1 Subtitle', type: 'text', defaultValue: 'Browse Our Floor Plans' },
    'hyperlocalexplore.card2_title': { label: 'Card 2 Title', type: 'text', defaultValue: 'Indulgent Details' },
    'hyperlocalexplore.card2_subtitle': { label: 'Card 2 Subtitle', type: 'text', defaultValue: 'Explore Our Amenities' },
    'hyperlocalexplore.card3_title': { label: 'Card 3 Title', type: 'text', defaultValue: 'Luxury Living' },
    'hyperlocalexplore.card3_subtitle': { label: 'Card 3 Subtitle', type: 'text', defaultValue: 'View the Gallery' },
  },
  slotGroups: {
    'hyperlocalexplore.card1_image': card1ImageDef,
    'hyperlocalexplore.card2_image': card2ImageDef,
    'hyperlocalexplore.card3_image': card3ImageDef,
    'hyperlocalexplore.card1_link': card1LinkDef,
    'hyperlocalexplore.card2_link': card2LinkDef,
    'hyperlocalexplore.card3_link': card3LinkDef,
  },
};

(HyperlocalExplore as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HyperlocalExplore;
